import { useState, useEffect, useMemo, useCallback } from 'react';
import './Fireworks.scss';

// Each burst takes one of the theme's three accents, so it follows the colour theme
const TONES = ['var(--primary-color)', 'var(--accent-secondary)', 'var(--accent-tertiary)'];

const Fireworks = ({ count = 8, particlesPerFirework = 12 }) => {
  const fireworkTypes = useMemo(() => ['burst', 'ring', 'willow', 'spiral', 'double-burst'], []);

  // Generate random positions, tones, and types for fireworks
  const generateFireworks = useCallback(() => {
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      x: `${10 + Math.random() * 80}%`,
      y: `${10 + Math.random() * 50}%`,
      delay: `${i * 1.2}s`,
      tone: TONES[Math.floor(Math.random() * TONES.length)],
      type: fireworkTypes[Math.floor(Math.random() * fireworkTypes.length)],
    }));
  }, [count, fireworkTypes]);

  const [fireworks, setFireworks] = useState(generateFireworks);
  const [key, setKey] = useState(0);

  // Regenerate firework positions, tones, and types after each animation cycle completes
  useEffect(() => {
    // Calculate total cycle duration to ensure all animations finish
    // Animation durations vary by type: burst(2.5s), ring(2.5s), willow(3s), spiral(2.8s), double-burst(3s)
    // Last firework delay: (count - 1) * 1.2s
    // Total time: last firework delay + longest animation duration
    const lastFireworkDelay = (count - 1) * 1200;
    const animationDuration = 3000; // Use longest animation duration (willow, double-burst)
    const totalCycleDuration = lastFireworkDelay + animationDuration;

    // Regenerate fireworks after each complete cycle
    const interval = setInterval(() => {
      setFireworks(generateFireworks());
      setKey((prev) => prev + 1); // Force React to recreate elements and restart CSS animations
    }, totalCycleDuration);

    return () => {
      clearInterval(interval);
    };
  }, [generateFireworks, count]);

  const particles = useMemo(() => {
    return Array.from({ length: particlesPerFirework }, (_, j) => ({
      id: j,
      angle: `${j * (360 / particlesPerFirework)}deg`,
      // Alternate long and short streaks so a burst doesn't read as a perfect asterisk
      len: j % 2 ? '7px' : '11px',
    }));
  }, [particlesPerFirework]);

  return (
    <div className="fireworks-background">
      {fireworks.map((firework) => (
        <div
          key={`${key}-${firework.id}`}
          className={`fireworks fireworks--${firework.type}`}
          style={{
            '--x': firework.x,
            '--y': firework.y,
            '--delay': firework.delay,
            '--spark': firework.tone,
          }}
        >
          {particles.map((particle) => (
            <div
              key={particle.id}
              className="particle"
              style={{
                '--angle': particle.angle,
                '--len': particle.len,
              }}
            />
          ))}
        </div>
      ))}
    </div>
  );
};

export default Fireworks;
